import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

type RealtimeEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

interface UseRealtimeOptions<T> {
  table: string;
  events?: RealtimeEvent[];
  filter?: string;
  onInsert?: (payload: RealtimePostgresChangesPayload<T>) => void;
  onUpdate?: (payload: RealtimePostgresChangesPayload<T>) => void;
  onDelete?: (payload: RealtimePostgresChangesPayload<T>) => void;
  onAny?: (payload: RealtimePostgresChangesPayload<T>) => void;
  enabled?: boolean;
}

export function useRealtime<T extends Record<string, unknown>>({
  table,
  events = ['INSERT', 'UPDATE', 'DELETE'],
  filter,
  onInsert,
  onUpdate,
  onDelete,
  onAny,
  enabled = true,
}: UseRealtimeOptions<T>) {
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const handler = useCallback(
    (payload: RealtimePostgresChangesPayload<T>) => {
      if (onAny) {
        onAny(payload);
        return;
      }
      switch (payload.eventType) {
        case 'INSERT':
          onInsert?.(payload);
          break;
        case 'UPDATE':
          onUpdate?.(payload);
          break;
        case 'DELETE':
          onDelete?.(payload);
          break;
      }
    },
    [onInsert, onUpdate, onDelete, onAny]
  );

  useEffect(() => {
    if (!enabled) return;

    const channelName = `${table}-${Math.random().toString(36).slice(2, 9)}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes' as never,
        {
          event: events.length === 1 ? events[0] : '*',
          schema: 'public',
          table,
          ...(filter ? { filter } : {}),
        },
        handler as never
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
    };
  }, [table, events.join(','), filter, handler, enabled]);
}